import { LOCALE_ID } from '@angular/core';
import { Route } from '@angular/router';
import { portalGuard } from '@/app/core/auth/guards/auth.guard';
import { localeReadyGuard, readStoredPortalLocale } from '@/app/core/i18n/portal-locale';
import {
  platformOnlyPathMatcher,
  redirectToPlatformApp,
  tenantHostOnly,
} from '@/app/core/tenant-host/tenant-host.guards';

export const routes: Route[] = [
  // KİRACI HOST'UNDA platform yolları ('/portal', '/admin', '/auth', '/site/<id>'…)
  // açılmaz → aynı yol app.melbarrel.com'da açılır. EN BAŞTA olmak zorunda:
  // aşağıdaki rotalar eşleşmeden yakalanmalı. Platform host'unda canMatch
  // false döner ve bu satır hiç yokmuş gibi davranılır. Liste ve gerekçe:
  // core/tenant-host/tenant-host.ts → isPlatformOnlyPath.
  // Sunucuda bu yollar Angular'a gelmeden server.ts'te 302 ile gönderiliyor.
  {
    matcher: platformOnlyPathMatcher,
    canMatch: [tenantHostOnly],
    canActivate: [redirectToPlatformApp],
    children: [],
  },
  // Tam ekran site editörü (portal kabuğu/menüsü YOK) — yeni sekmede açılır.
  {
    path: 'builder/:id',
    canActivate: [portalGuard],
    loadComponent: () => import('./domains/portal/modules/web-builder/portal-wb-sections'),
  },
  // Editör mobil önizlemesi (iframe içinde dar viewport) — taslak render, auth gerekir.
  {
    path: 'preview/:pageId',
    canActivate: [portalGuard],
    loadComponent: () => import('./domains/site/site-preview.page'),
  },
  // Public sözleşme imza sayfası (giriş gerektirmez, guard yok) — '' rotasından ÖNCE.
  {
    path: 'sign/:token',
    loadComponent: () => import('./domains/public/sign.page'),
  },
  // Ticari e-postadaki "Abonelikten çık" bağlantısı (CASL) — giriş yok, her host'ta; '' rotalarından ÖNCE.
  {
    path: 'unsubscribe/:token',
    loadComponent: () => import('./domains/public/unsubscribe.page'),
  },
  // Public müfettiş başvuru (intake) sayfası (giriş gerektirmez) — '' rotasından ÖNCE.
  {
    path: 'inspection-intake/:token',
    loadComponent: () => import('./domains/public/inspection-intake.page'),
  },
  // Herkese açık Melboard panosu — salt okunur, giriş yok. Kapı anahtarın
  // kendisi, süresi ve iptal damgası; üçü de sunucuda denetleniyor.
  {
    path: 'pano/:token',
    loadComponent: () => import('./domains/public/board.page'),
  },

  // Müşterinin ön-yüz (public) web sitesi — ziyaretçi görünümü (giriş yok).
  // PLATFORM host'unda /site/:clientId: editör önizlemesi ve kiracının yedek
  // adresi bunlara dayanıyor. Kiracının kendi alan adında aynı sayfalar KÖKTEN
  // açılır (aşağıda, tanıtım sitesinden hemen önce: tenant-site.routes).
  // Mağaza rotaları ':slug'dan ÖNCE gelmeli — aksi hâlde 'urun' ve 'sepet'
  // birer sayfa slug'ı sanılıp site-public.page'e düşer ve "sayfa bulunamadı" olur.
  {
    path: 'site/:clientId/urun/:productId',
    loadComponent: () => import('./domains/site/shop-product.page'),
  },
  {
    path: 'site/:clientId/sepet',
    loadComponent: () => import('./domains/site/shop-cart.page'),
  },
  {
    path: 'site/:clientId/:slug',
    loadComponent: () => import('./domains/site/site-public.page'),
  },
  {
    path: 'site/:clientId',
    loadComponent: () => import('./domains/site/site-public.page'),
  },

  // QR menü — MİSAFİR sayfası. Portal ağacının DIŞINDA ve guard'sız:
  // masadaki karekodu okutan kişinin hesabı yoktur. Portalın altında olsaydı
  // portalGuard misafiri giriş ekranına atardı.
  //
  // localeReadyGuard gerekli: sayfa CurrencyPipe kullanıyor ve biçim verisi
  // kayıtlı değilse Angular "Missing locale data" ile sayfayı hiç çizmiyor.
  {
    path: 'qr/:token',
    canActivate: [localeReadyGuard],
    loadComponent: () => import('./domains/public-qr/public-qr-menu'),
  },

  // Auth
  {
    path: 'auth',
    loadChildren: () => import('./domains/auth/routes'),
  },

  // Kayıt sonrası kurulum — portal AĞACININ DIŞINDA.
  // Portalın altında olsaydı portalGuard/modül kapıları çalışır ve henüz
  // paketi olmayan müşteri kendi kurulum ekranına giremezdi.
  {
    path: 'onboarding',
    loadChildren: () => import('./domains/onboarding/routes'),
    // Sözlük ve biçim verisi BURADA yükleniyor.
    //
    // Guard olmadan Transloco sözlüğü hiç yüklenmiyor ve ekranda çeviri yerine
    // ANAHTAR ADI görünüyor — tarayıcı testinde m-select yer tutucuları
    // "common.labels.choose" diye çıktı. LOCALE_ID de gerekli: paket kartları
    // CurrencyPipe kullanıyor ve biçim verisi kayıtlı değilse Angular
    // "Missing locale data" ile sayfayı hiç çizmiyor.
    canActivate: [localeReadyGuard],
    providers: [{ provide: LOCALE_ID, useFactory: readStoredPortalLocale }],
  },

  // Admin (platform admin paneli)
  {
    path: 'admin',
    loadChildren: () => import('./domains/admin/routes'),
    // Panel ile AYNI sözlüğü kullanıyor: impersonation ve oturum-süresi
    // bantları core/auth altında paylaşılıyor, ikisi de burada çiziliyor.
    // Bekçi olmadan sözlük hiç yüklenmez ve bantlarda çeviri yerine anahtar
    // adı ('session.expiredTitle') görünür.
    canActivate: [localeReadyGuard],
    providers: [{ provide: LOCALE_ID, useFactory: readStoredPortalLocale }],
  },

  // Portal (müşteri paneli)
  {
    path: 'portal',
    loadChildren: () => import('./domains/portal/routes'),
    // LOCALE_ID burada, KÖKTE değil.
    //
    // Kökte olsaydı kullanıcının panel dili SSR ile çizilen vitrini de
    // etkilerdi: sunucu 'en-CA' ile render eder, istemci localStorage'daki
    // 'tr' ile hidrate ederdi ve her tarih metninde hidrasyon uyuşmazlığı
    // çıkardı. Rota düzeyinde sağlanınca yalnız portal ağacındaki borular
    // (DatePipe/DecimalPipe/CurrencyPipe) bu değeri görür; vitrin dokunulmadan
    // kalır. Portal zaten RenderMode.Client — burada SSR yok.
    //
    // localeReadyGuard, LOCALE_ID'nin gösterdiği dilin biçim verisi kayıtlı
    // olmadan hiçbir borunun çalışmamasını garantiler — aksi hâlde Angular
    // "Missing locale data" ile sayfayı hiç çizmez.
    canActivate: [localeReadyGuard],
    providers: [{ provide: LOCALE_ID, useFactory: readStoredPortalLocale }],
  },

  // /admin/cms önizlemesinin iframe sayfası. Vitrin bölümlerini GERÇEK bir
  // ekran genişliğinde (kırılma noktaları çalışsın) ve vitrin kökünde (.mkt)
  // çizer; içerik açan panelden postMessage ile gelir. Vitrin ağacından
  // (':lang') ÖNCE olmalı, yoksa 'cms-preview' bir dil kodu sanılırdı.
  {
    path: 'cms-preview',
    loadComponent: () => import('./domains/website/features/cms-preview.page'),
  },

  // KİRACI VİTRİNİ — kiracının kendi alan adında (musteri.com ya da
  // <slug>.melbarrel.com) kök '/'. Kök rotanın host'a göre İKİ KOLU var: bu
  // kol yalnız kiracı host'unda eşleşir; platform host'unda canMatch false
  // döner ve aşağıdaki tanıtım sitesi kolu çalışır. Bilinmeyen host'ta da
  // tanıtım sitesine DÜŞÜLMEZ — "site bulunamadı" (404) çizilir.
  {
    path: '',
    canMatch: [tenantHostOnly],
    loadChildren: () => import('./domains/site/tenant-site.routes'),
  },

  // melbarrel.com vitrini — EN SONDA olmak ZORUNDA.
  //
  // Dil önekli ('/:lang/...') ve altında CMS sayfaları için bir catch-all var.
  // Yukarı taşınırsa /admin isteği :lang='admin' olarak buraya düşer ve panel
  // tamamen erişilemez hâle gelir. Kendi 404'ünü de kendi içinde çözüyor, o
  // yüzden app seviyesinde ayrı bir '**' rotası yok.
  {
    path: '',
    loadChildren: () => import('./domains/website/routes'),
  },
];
